// PARSING A PASTED PO TABLE. Pure, no React, no network, no side effects.
//
// ⚠️ WHY THIS EXISTS. The PO comparison has never been run against a real
// multi-line PO. The only sample is one line at quantity 1, where Price,
// Ordered, Line Total and Total are all the same number -- so it cannot even
// distinguish a unit price from an extended one. Typing a twenty-line PO by
// hand to find out is why nobody has.
//
// ⚠️ WHAT THIS IS NOT. It does not read documents. A person copies rows out of
// a PDF or a spreadsheet and pastes them; this turns that text into rows they
// then CONFIRM. `line_origin` stays "manual" because a person confirmed every
// row -- see the review step in PoComparison. Extraction, when it comes, plugs
// into the same review seam and earns its own origin value.
//
// ⚠️ THE THREE RULES THIS MODULE IS BUILT ON:
//
//   1. ABSENT IS NOT ZERO. A cell this module cannot read becomes
//      `{ kind: "unreadable" }`, never 0 and never a guess. A quantity of 0 on
//      a PO would compare as a real claim about what the customer ordered.
//   2. IT REFUSES RATHER THAN GUESSES. Where the figures cannot be told apart
//      the row is FLAGGED for the person. See `ambiguity` below for exactly
//      what that means.
//   3. IT DECIDES COLUMNS FROM THE WHOLE PASTE, NOT ROW BY ROW. One row can be
//      coincidentally consistent; five rows agreeing is evidence.

/** A field that was read, or one that could not be. */
export type Cell =
  | { kind: "value"; text: string }
  | { kind: "unreadable"; reason: string };

export type NumberCell =
  | { kind: "value"; text: string; n: number }
  | { kind: "unreadable"; reason: string };

export type ParsedRow = {
  /** 1-based, as the person sees it in the review table. */
  index: number;
  partNumber: Cell;
  quantity: NumberCell;
  unitPrice: NumberCell;
  description: Cell;
  manufacturer: Cell;
  /** The raw fields, so the review table can show what it was working from. */
  raw: string[];
  /** Set when this row's own figures contradict the paste's column roles. */
  flagged: boolean;
  flagReason: string;
};

export type Delimiter = "tab" | "comma" | "multispace" | "none";

export type ParseResult = {
  rows: ParsedRow[];
  delimiter: Delimiter;
  /** A header row that was recognised and skipped, for the report line. */
  skippedHeader: string | null;
  /**
   * ⚠️ SET WHEN THE COLUMN ROLES COULD NOT BE DETERMINED FOR THE WHOLE PASTE.
   * The rows are still returned, with their raw fields, so the person can see
   * what arrived -- but quantity and unitPrice are `unreadable` rather than a
   * guess, and the UI must not let them be saved as they stand.
   */
  ambiguous: boolean;
  ambiguityReason: string;
};

// --- numbers --------------------------------------------------------------
//
// ⚠️ THE GROUPING PATTERN ACCEPTS BOTH CONVENTIONS AND NOTHING ELSE.
//   Western: 1,234.56      groups of three
//   Indian:  1,23,456.78   a group of three, then twos
// `^\d{1,3}(,\d{2,3})*(\.\d+)?$` accepts both and REJECTS `1,2,3`, which is
// neither. Stripping every comma unconditionally would turn `1,2,3` into 123 --
// a confident wrong number, which is the one outcome this module exists to
// avoid.
const GROUPED = /^\d{1,3}(?:,\d{2,3})*(?:\.\d+)?$/;
const PLAIN = /^\d+(?:\.\d+)?$/;

// ₹, Rs, Rs., INR, $ and any surrounding space. Stripped before the number is
// tested, never after -- "Rs 7,128.00" must read as one figure.
const CURRENCY = /(?:₹|rs\.?|inr|\$)/gi;

/** The number in a cell, or null if it is not one. */
export function parseNumber(text: string): number | null {
  const bare = text.replace(CURRENCY, "").replace(/\s+/g, "").trim();
  if (bare === "") return null;
  // A trailing minus or parentheses would be a credit note, not a PO line.
  if (/^[-(]/.test(bare)) return null;
  if (PLAIN.test(bare)) return Number(bare);
  if (GROUPED.test(bare)) return Number(bare.replace(/,/g, ""));
  return null;
}

const isNumeric = (text: string) => parseNumber(text) !== null;

// --- splitting ------------------------------------------------------------
//
// ⚠️ ONE DELIMITER FOR THE WHOLE PASTE, chosen in this order. Mixing them per
// line would let two rows of the same document be split differently, which is
// how a quantity ends up in a price column.
//
// ⚠️ AND COMMA IS LAST FOR A REASON: it collides with thousands separators.
// `PART, 23, 7,128.00` split naively on commas yields five fields, two of them
// halves of one number. Tab and multi-space are tried first because they are
// unambiguous; comma is only reached when neither appears.
function chooseDelimiter(lines: string[]): Delimiter {
  if (lines.some((l) => l.includes("\t"))) return "tab";
  if (lines.some((l) => /\S {2,}\S/.test(l))) return "multispace";
  if (lines.some((l) => l.includes(","))) return "comma";
  return "none";
}

/**
 * ⚠️ REJOINING A NUMBER THAT COMMA-SPLITTING TORE IN HALF. After splitting on
 * commas, a fragment of exactly two or three digits following a fragment that
 * ends in a digit is a thousands group, not a column. `7` + `128.00` becomes
 * `7,128.00`, which parseNumber then reads.
 *
 * Applied ONLY to the comma delimiter, because it is the only one with this
 * problem. It is conservative: a two-or-three-digit fragment that is genuinely
 * its own column (a quantity of 23, say) after a NUMERIC column would be
 * absorbed -- which is exactly the shape this function cannot distinguish, and
 * is why `comma` is the last delimiter tried rather than the first.
 */
function rejoinGroupedNumbers(fields: string[]): string[] {
  const out: string[] = [];
  for (const field of fields) {
    const prev = out[out.length - 1];
    const trimmed = field.trim();
    if (
      prev !== undefined &&
      /\d$/.test(prev.trim()) &&
      /^\d{2,3}(\.\d+)?$/.test(trimmed) &&
      // Only if the join actually produces a valid grouped number. Without
      // this check the rejoin would fire on any two adjacent numbers.
      GROUPED.test(`${prev.trim()},${trimmed}`)
    ) {
      out[out.length - 1] = `${prev.trim()},${trimmed}`;
      continue;
    }
    out.push(field);
  }
  return out;
}

function splitLine(line: string, delimiter: Delimiter, rejoin = true): string[] {
  switch (delimiter) {
    case "tab":
      return line.split("\t").map((f) => f.trim());
    case "comma":
      return (rejoin ? rejoinGroupedNumbers(line.split(",")) : line.split(","))
        .map((f) => f.trim());
    case "multispace":
      // ⚠️ EMPTY CELLS ARE LOST HERE, unavoidably: runs of spaces collapse, so
      // a blank quantity between two values is invisible. Stated in the UI as
      // well -- a paste from a PDF is usually space-aligned, and a person
      // needs to know a missing cell may simply not have survived the copy.
      return line.trim().split(/ {2,}|\t/).map((f) => f.trim());
    case "none":
      return [line.trim()];
  }
}

// --- the header row -------------------------------------------------------
const HEADER_WORDS = [
  "part", "part no", "part number", "item", "sku", "code", "model",
  "qty", "quantity", "ordered", "nos", "units",
  "rate", "price", "unit price", "unit rate", "amount", "value", "total",
  "description", "particulars", "make", "manufacturer", "brand", "oem",
  "sr", "sr no", "s.no", "sl", "sl no", "#",
];

/** A first row that names columns rather than stating any. */
function looksLikeHeader(fields: string[]): boolean {
  const cells = fields.map((f) => f.trim().toLowerCase()).filter((f) => f !== "");
  if (cells.length < 2) return false;
  // A header states no figures at all, and at least half its cells are words
  // we recognise as column names. Both halves matter: "NRS-PD-48 Cat6 cable"
  // has no numbers either, and must not be mistaken for a header.
  if (cells.some(isNumeric)) return false;
  const known = cells.filter((c) => HEADER_WORDS.includes(c)).length;
  return known >= Math.ceil(cells.length / 2);
}

// --- column roles ---------------------------------------------------------
//
// ⚠️ THIS IS THE PART THAT REFUSES TO GUESS, and the definition of "ambiguous"
// lives here.
//
// A real PO row is some arrangement of: a part number, maybe a description,
// maybe a make, a quantity, a unit price, and very often a LINE TOTAL. The
// danger is picking the line total as the unit price -- on the one sample PO
// available they are the same number, because the quantity is 1, so nothing on
// that document could tell them apart.
//
// THE TEST IS ARITHMETIC, ACROSS EVERY ROW: quantity x unit price = line total.
// If exactly one ordered pair of numeric columns (q, p) has a third column t
// where q x p = t for EVERY row that has all three, those are the quantity and
// the unit price. One row can satisfy that by coincidence; a whole paste
// cannot.
//
// AMBIGUOUS means, precisely:
//   - three or more numeric columns and NO (q, p, t) triple holds across the
//     rows -- so there is no evidence which column is the unit price; or
//   - three or more numeric columns and MORE THAN ONE distinct (q, p) pair
//     satisfies it -- so the evidence points two ways at once; or
//   - exactly one numeric column, and no empty cell to say which role it is
//     playing.
//
// Two numeric columns are NOT ambiguous: quantity then unit price, by
// position, which is the order every PO in evidence uses and the only order
// two columns can be read in.
const PRODUCT_TOLERANCE = 0.02; // two paise on a line total

type Roles = {
  partIdx: number | null;
  qtyIdx: number | null;
  priceIdx: number | null;
  descIdx: number | null;
  makeIdx: number | null;
  ambiguous: boolean;
  reason: string;
};

function numericColumns(grid: string[][], width: number): number[] {
  const cols: number[] = [];
  for (let c = 0; c < width; c += 1) {
    const cells = grid.map((r) => (r[c] ?? "").trim()).filter((v) => v !== "");
    if (cells.length > 0 && cells.every(isNumeric)) cols.push(c);
  }
  return cols;
}

function productHolds(grid: string[][], q: number, p: number, t: number): boolean {
  let tested = 0;
  for (const row of grid) {
    const qn = parseNumber(row[q] ?? "");
    const pn = parseNumber(row[p] ?? "");
    const tn = parseNumber(row[t] ?? "");
    if (qn === null || pn === null || tn === null) continue;
    if (Math.abs(qn * pn - tn) > PRODUCT_TOLERANCE) return false;
    tested += 1;
  }
  return tested > 0;
}

function assignRoles(grid: string[][], width: number): Roles {
  const numeric = numericColumns(grid, width);
  const numericSet = new Set(numeric);

  // The part number is the FIRST non-numeric column with something in it. A
  // leading serial-number column is numeric and so is skipped for free.
  let partIdx: number | null = null;
  const textCols: number[] = [];
  for (let c = 0; c < width; c += 1) {
    if (numericSet.has(c)) continue;
    const any = grid.some((r) => (r[c] ?? "").trim() !== "");
    if (!any) continue;
    textCols.push(c);
    if (partIdx === null) partIdx = c;
  }
  // The longest remaining text column is the description; a shorter one before
  // it is a make. Length is the only signal available and it is a weak one, so
  // both are optional and neither can affect a figure.
  const others = textCols.filter((c) => c !== partIdx);
  let descIdx: number | null = null;
  let makeIdx: number | null = null;
  if (others.length === 1) {
    descIdx = others[0];
  } else if (others.length >= 2) {
    const avgLen = (c: number) =>
      grid.reduce((s, r) => s + (r[c] ?? "").trim().length, 0) / Math.max(1, grid.length);
    const sorted = [...others].sort((a, b) => avgLen(b) - avgLen(a));
    descIdx = sorted[0];
    makeIdx = sorted[1];
  }

  const base = { partIdx, descIdx, makeIdx };

  if (numeric.length === 0) {
    return { ...base, qtyIdx: null, priceIdx: null, ambiguous: false,
      reason: "no numeric column was found, so no quantity or price was read" };
  }

  if (numeric.length === 1) {
    // ⚠️ ONE NUMBER AND NOTHING TO SAY WHICH IT IS. A single figure could be a
    // quantity or a price, and choosing would be exactly the guess this
    // refuses. The only way out is an EMPTY CELL elsewhere saying the other
    // role was left blank -- which only survives a delimiter that preserves
    // empty cells, so this is resolved in the row pass, not here.
    return { ...base, qtyIdx: null, priceIdx: numeric[0], ambiguous: true,
      reason: "only one numeric column was found, so it is not possible to tell a quantity from a unit price" };
  }

  if (numeric.length === 2) {
    return { ...base, qtyIdx: numeric[0], priceIdx: numeric[1], ambiguous: false,
      reason: "two numeric columns: quantity then unit price, by position" };
  }

  // Three or more: the arithmetic decides, or nothing does.
  const hits: Array<{ q: number; p: number; t: number }> = [];
  for (const q of numeric) {
    for (const p of numeric) {
      if (p === q) continue;
      for (const t of numeric) {
        if (t === q || t === p) continue;
        if (productHolds(grid, q, p, t)) hits.push({ q, p, t });
      }
    }
  }
  // q x p = t and p x q = t are the same finding; collapse them.
  const pairs = new Map<string, { q: number; p: number }>();
  for (const h of hits) {
    const key = [h.q, h.p].sort((a, b) => a - b).join("x");
    if (!pairs.has(key)) {
      // The quantity is the column whose values are the smaller, more
      // integral ones -- but ONLY as a tie-break within an already-proven
      // pair, never as the evidence itself.
      const integrality = (c: number) =>
        grid.filter((r) => {
          const n = parseNumber(r[c] ?? "");
          return n !== null && Number.isInteger(n);
        }).length;
      const qFirst = integrality(h.q) >= integrality(h.p);
      pairs.set(key, qFirst ? { q: h.q, p: h.p } : { q: h.p, p: h.q });
    }
  }

  if (pairs.size === 1) {
    const only = [...pairs.values()][0];
    return { ...base, qtyIdx: only.q, priceIdx: only.p, ambiguous: false,
      reason: "quantity x unit price matched a line total across every row" };
  }
  if (pairs.size === 0) {
    return { ...base, qtyIdx: null, priceIdx: null, ambiguous: true,
      reason: `${numeric.length} numeric columns and none of them multiply out to another, so which one is the unit price cannot be told from the figures` };
  }
  return { ...base, qtyIdx: null, priceIdx: null, ambiguous: true,
    reason: `${numeric.length} numeric columns and ${pairs.size} different pairs multiply out, so the figures point more than one way` };
}

// --- the whole thing ------------------------------------------------------
const UNREADABLE = (reason: string): { kind: "unreadable"; reason: string } =>
  ({ kind: "unreadable", reason });

function textCell(grid: string[][], row: number, idx: number | null): Cell {
  if (idx === null) return UNREADABLE("no column was identified for this field");
  const v = (grid[row][idx] ?? "").trim();
  return v === "" ? UNREADABLE("the cell is empty") : { kind: "value", text: v };
}

function numberCell(grid: string[][], row: number, idx: number | null): NumberCell {
  if (idx === null) return UNREADABLE("no column was identified for this field");
  const v = (grid[row][idx] ?? "").trim();
  // ⚠️ AN EMPTY CELL IS UNREADABLE, NOT ZERO. A quantity of 0 on a PO is a
  // claim about what the customer ordered; a blank cell is the absence of one.
  if (v === "") return UNREADABLE("the cell is empty");
  const n = parseNumber(v);
  if (n === null) return UNREADABLE(`"${v}" is not a number this can read`);
  return { kind: "value", text: v, n };
}

export function parsePastedPoLines(text: string): ParseResult {
  const lines = text
    .split(/\r\n|\r|\n/)
    .map((l) => l.replace(/\s+$/, ""))
    .filter((l) => l.trim() !== "");

  if (lines.length === 0) {
    return { rows: [], delimiter: "none", skippedHeader: null,
      ambiguous: false, ambiguityReason: "nothing was pasted" };
  }

  const delimiter = chooseDelimiter(lines);
  // ⚠️ COMMA INPUT IS READ TWICE AND THE BETTER READING WINS, because a comma
  // is both a column separator and a thousands separator and no single split
  // can be right for both:
  //
  //   "NKJ6AS-BL,10,250.00"      naive -> 3 fields, 2 numerics: CLEAN
  //                              rejoined -> "10,250.00", 1 numeric: AMBIGUOUS
  //   "NRS-PD-48,23,7,128.00"    naive -> 4 fields; 23, 7 and 128.00 do not
  //                              multiply out: AMBIGUOUS
  //                              rejoined -> "7,128.00", 2 numerics: CLEAN
  //
  // The two want OPPOSITE treatment and are indistinguishable by shape alone,
  // so the rule is to prefer whichever reading the column assignment can
  // actually resolve.
  //
  // ⚠️ AN EAGER REJOIN SILENTLY TURNED A QUANTITY OF 10 AND A PRICE OF 250.00
  // INTO ONE FIGURE OF 10,250.00 -- the confident wrong number this module
  // exists to avoid. It was caught by the plain comma-separated test, not by
  // reasoning about it, which is why the test came first.
  let grid = lines.map((l) => splitLine(l, delimiter));
  if (delimiter === "comma") {
    const naive = lines.map((l) => splitLine(l, delimiter, false));
    const body = naive.length > 1 && looksLikeHeader(naive[0]) ? naive.slice(1) : naive;
    const naiveWidth = body.reduce((w, r) => Math.max(w, r.length), 0);
    if (body.length > 0 && naiveWidth >= 2) {
      const naiveRoles = assignRoles(body, naiveWidth);
      if (!naiveRoles.ambiguous) grid = naive;
    }
  }

  let skippedHeader: string | null = null;
  if (grid.length > 1 && looksLikeHeader(grid[0])) {
    skippedHeader = lines[0];
    grid = grid.slice(1);
  }

  if (grid.length === 0) {
    return { rows: [], delimiter, skippedHeader,
      ambiguous: false, ambiguityReason: "the paste held only a header row" };
  }

  const width = grid.reduce((w, r) => Math.max(w, r.length), 0);

  if (delimiter === "none" || width < 2) {
    // One field per line: there are no columns to assign. The rows come back
    // so the person can see what arrived, with nothing invented.
    return {
      delimiter, skippedHeader, ambiguous: true,
      ambiguityReason: "the pasted text has only one column, so a part number, quantity and price could not be told apart. Paste rows with tabs, commas or aligned spacing between the columns.",
      rows: grid.map((raw, i) => ({
        index: i + 1, raw,
        partNumber: raw[0] ? { kind: "value" as const, text: raw[0] } : UNREADABLE("the cell is empty"),
        quantity: UNREADABLE("no column was identified for this field"),
        unitPrice: UNREADABLE("no column was identified for this field"),
        description: UNREADABLE("no column was identified for this field"),
        manufacturer: UNREADABLE("no column was identified for this field"),
        flagged: true,
        flagReason: "only one column was found in the pasted text",
      })),
    };
  }

  const roles = assignRoles(grid, width);

  const rows: ParsedRow[] = grid.map((raw, i) => {
    const quantity = numberCell(grid, i, roles.qtyIdx);
    const unitPrice = numberCell(grid, i, roles.priceIdx);

    // ⚠️ A ROW THAT CONTRADICTS THE PASTE'S OWN ROLES IS FLAGGED. The columns
    // were chosen from every row together; one row whose figures do not fit is
    // the person's to look at, not this module's to reinterpret.
    let flagged = roles.ambiguous;
    let flagReason = roles.ambiguous ? roles.reason : "";
    if (!flagged && quantity.kind === "unreadable" && unitPrice.kind === "unreadable") {
      flagged = true;
      flagReason = "neither a quantity nor a unit price could be read on this row";
    }

    return {
      index: i + 1,
      raw,
      partNumber: textCell(grid, i, roles.partIdx),
      quantity,
      unitPrice,
      description: textCell(grid, i, roles.descIdx),
      manufacturer: textCell(grid, i, roles.makeIdx),
      flagged,
      flagReason,
    };
  });

  return {
    rows, delimiter, skippedHeader,
    ambiguous: roles.ambiguous,
    ambiguityReason: roles.ambiguous ? roles.reason : "",
  };
}

/** How the review table labels a cell it could not read. */
export const UNREADABLE_LABEL = "UNREADABLE";
